import { useEffect, useMemo, useRef, useState } from "react";

import { ApiError } from "../api/client";
import type {
  OpportunityFieldDefinition,
  OpportunityFieldSettings,
  OpportunityFieldsLoader,
  OpportunityFieldType,
  OpportunityLayout,
  OpportunityPersonalView,
} from "../api/client";
import {
  activeColumnKeys,
  columnLabel,
  fieldErrorText,
  parseOpportunityFieldSettings,
  parseOpportunityPersonalView,
  reconcileOpportunityLayout,
} from "./opportunityFields";
import { OpportunityPanelToggle } from "./OpportunityPanelToggle";

const inputClass = "rounded border border-border bg-background px-3 py-2 text-sm";
const MAX_LABEL = 80;
const MAX_OPTIONS = 20;
// Stable identity for the virtual (no settings yet) catalogue. Without this the
// `?? []` fallback is re-created on every render and invalidates `activeColumns`.
const EMPTY_DEFINITIONS: OpportunityFieldDefinition[] = [];

function move(order: string[], index: number, change: number): string[] {
  const target = index + change;
  // Organization is the fixed identity column; selection and Details are not
  // columns at all. Only the display columns after it may be reordered.
  if (index < 1 || target < 1 || target >= order.length) return order;
  const next = [...order];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

function layoutsEqual(left: OpportunityLayout, right: OpportunityLayout): boolean {
  return left.order.length === right.order.length
    && left.order.every((key, index) => key === right.order[index])
    && left.hidden.length === right.hidden.length
    && left.hidden.every((key, index) => key === right.hidden[index]);
}

export function OpportunityFieldControls({
  projectId,
  currentUserId,
  settings,
  personalView,
  layout,
  canManage,
  loader,
  busy,
  onLayout,
  onSettings,
  onPersonal,
  onCatalogChanged,
}: {
  projectId: string;
  currentUserId?: string;
  settings: OpportunityFieldSettings | null;
  personalView: OpportunityPersonalView | null;
  layout: OpportunityLayout;
  canManage: boolean;
  loader: OpportunityFieldsLoader;
  busy: boolean;
  onLayout: (layout: OpportunityLayout) => void;
  onSettings: (settings: OpportunityFieldSettings) => void;
  onPersonal: (view: OpportunityPersonalView | null) => void;
  onCatalogChanged: (settings: OpportunityFieldSettings) => void;
}) {
  const [pending, setPending] = useState("");
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [notice, setNotice] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newType, setNewType] = useState<OpportunityFieldType>("text");
  const [newOptions, setNewOptions] = useState("");
  const [optionDrafts, setOptionDrafts] = useState<Record<string, string>>({});
  // Presentation only: the body stays mounted and keeps pending writes, dirty
  // drafts, field ids and generation guards alive while compact.
  const [collapsed, setCollapsed] = useState(true);
  const projectGeneration = useRef(0);

  const definitions = useMemo(
    () => settings?.definitions ?? EMPTY_DEFINITIONS,
    [settings?.definitions],
  );
  const activeColumns = useMemo(() => activeColumnKeys(definitions), [definitions]);
  const personalActive = Boolean(personalView && !personalView.use_project_default);
  const viewLabel = personalActive ? "Osobni prikaz" : "Projektni zadani prikaz";
  const usableLayout = useMemo(() => {
    const order = layout.order.filter((key) => activeColumns.includes(key));
    for (const key of activeColumns) if (!order.includes(key)) order.push(key);
    return {
      order,
      hidden: layout.hidden.filter(
        (key) => order.includes(key) && key !== "builtin:organization",
      ),
    };
  }, [activeColumns, layout]);
  const persistedLayout = useMemo(() => {
    const base = personalActive && personalView?.layout
      ? personalView.layout
      : settings?.shared_layout;
    // `usableLayout` is reconciled against the current catalogue when it is read.
    // Compare against the same reconciled form so an archived key is not reported
    // as an unsaved operator edit.
    return base ? reconcileOpportunityLayout(base, definitions).layout : null;
  }, [personalActive, personalView?.layout, settings?.shared_layout, definitions]);
  const dirty = (persistedLayout !== null
    && !layoutsEqual(usableLayout, persistedLayout))
    || newLabel.trim() !== ""
    || newOptions.trim() !== ""
    || Object.values(optionDrafts).some((value) => value.trim() !== "");
  const savingLabel = pending === "personal"
    ? "spremam osobni prikaz"
    : pending === "default"
      ? "vraćam projektni zadani prikaz"
      : pending === "shared"
        ? "spremam zajednički raspored"
        : pending === "add-field"
          ? "dodajem timsko polje"
          : pending.startsWith("archive-field")
            ? "arhiviram timsko polje"
            : pending.startsWith("add-option")
              ? "dodajem opciju"
              : pending.startsWith("archive-option")
                ? "arhiviram opciju"
                : "spremam promjenu";

  useEffect(() => {
    // A write/refresh must not erase the explicit success notice it just produced.
    // A project or authenticated-user identity change retires every in-flight
    // continuation; a personal view response for the previous user must never be
    // adopted by the next session even when the project id is unchanged.
    projectGeneration.current += 1;
    setPending("");
    setError("");
    setErrorCode("");
    setNotice("");
    setCollapsed(true);
    return () => {
      projectGeneration.current += 1;
    };
  }, [projectId, currentUserId]);

  const run = async (
    kind: string,
    action: (generation: number) => Promise<void>,
  ) => {
    if (pending || busy) return;
    const generation = projectGeneration.current;
    setPending(kind);
    setError("");
    setErrorCode("");
    setNotice("");
    try {
      await action(generation);
    } catch (err) {
      if (generation === projectGeneration.current) {
        setError(fieldErrorText(err));
        setErrorCode(err instanceof ApiError ? err.detail ?? "error" : "error");
      }
    } finally {
      if (generation === projectGeneration.current) setPending("");
    }
  };

  const savePersonal = () => run("personal", async (generation) => {
    if (!settings) return;
    const raw = await loader.putOpportunityPersonalView(projectId, {
      version: personalView?.version ?? 0,
      layout: usableLayout,
    });
    if (generation !== projectGeneration.current) return;
    const parsed = parseOpportunityPersonalView(raw, projectId, currentUserId);
    if (parsed === null) throw new Error("personal_view_response_invalid");
    onPersonal(parsed);
    onLayout(parsed.layout ?? usableLayout);
    setNotice("Osobni prikaz spreman je za ovaj projekt i korisnika.");
  });

  const resetToProjectDefault = () => run("default", async (generation) => {
    if (!settings) return;
    const raw = await loader.putOpportunityPersonalView(projectId, {
      version: personalView?.version ?? 0,
      layout: null,
    });
    if (generation !== projectGeneration.current) return;
    const parsed = parseOpportunityPersonalView(raw, projectId, currentUserId);
    if (parsed === null || !parsed.use_project_default) {
      throw new Error("personal_view_response_invalid");
    }
    onPersonal(parsed);
    if (settings) onLayout(settings.shared_layout);
    setNotice("Vraćen je projektni zadani prikaz. Osobna postavka je spremljena.");
  });

  const saveShared = () => run("shared", async (generation) => {
    if (!settings || !canManage) return;
    const raw = await loader.putOpportunitySharedLayout(projectId, {
      version: settings.version,
      order: usableLayout.order,
      hidden: usableLayout.hidden,
    });
    if (generation !== projectGeneration.current) return;
    const parsed = parseOpportunityFieldSettings(raw, projectId);
    if (parsed === null) throw new Error("settings_response_invalid");
    onSettings(parsed);
    if (!personalActive) onLayout(parsed.shared_layout);
    setNotice("Zajednički zadani raspored sprema se na projekt.");
  });

  const addField = () => run("add-field", async (generation) => {
    if (!settings || !canManage) return;
    const label = newLabel.trim();
    if (!label) {
      setError("Zadajte naziv polja.");
      return;
    }
    const options = newType === "select"
      ? newOptions.split("\n").map((value) => value.trim()).filter(Boolean)
      : [];
    if (newType === "select" && (options.length < 1 || options.length > MAX_OPTIONS)) {
      setError("Polje s odabirom treba 1–20 opcija, jedna po redu.");
      return;
    }
    const raw = await loader.createOpportunityField(projectId, {
      version: settings.version,
      label,
      type: newType,
      ...(newType === "select" ? { options: options.map((value) => ({ label: value })) } : {}),
    });
    if (generation !== projectGeneration.current) return;
    const parsed = parseOpportunityFieldSettings(raw, projectId);
    if (parsed === null) throw new Error("settings_response_invalid");
    onCatalogChanged(parsed);
    setNewLabel("");
    setNewOptions("");
    setNotice("Timsko polje dodano je u projektni katalog.");
  });

  const archiveField = (definition: OpportunityFieldDefinition) => run(
    `archive-field:${definition.id}`,
    async (generation) => {
      if (!settings || !canManage) return;
      const raw = await loader.updateOpportunityField(projectId, definition.id, {
        version: settings.version,
        archive: true,
      });
      // The archive response belongs to the project/user generation that started
      // it. A delayed response after navigation must not apply A's catalogue to B.
      if (generation !== projectGeneration.current) return;
      const parsed = parseOpportunityFieldSettings(raw, projectId);
      if (parsed === null) throw new Error("settings_response_invalid");
      onCatalogChanged(parsed);
      setNotice(
        `Polje „${definition.label}“ arhivirano je. Postojeće vrijednosti ostaju sačuvane.`,
      );
    },
  );

  const addOption = (definition: OpportunityFieldDefinition) => run(
    `add-option:${definition.id}`,
    async (generation) => {
      if (!settings || !canManage) return;
      const label = (optionDrafts[definition.id] ?? "").trim();
      if (!label) {
        setError("Zadajte naziv opcije.");
        return;
      }
      const raw = await loader.updateOpportunityField(projectId, definition.id, {
        version: settings.version,
        add_options: [{ label }],
      });
      if (generation !== projectGeneration.current) return;
      const parsed = parseOpportunityFieldSettings(raw, projectId);
      if (parsed === null) throw new Error("settings_response_invalid");
      onCatalogChanged(parsed);
      setOptionDrafts((old) => ({ ...old, [definition.id]: "" }));
      setNotice("Opcija je dodana. Postojeće vrijednosti nisu promijenjene.");
    },
  );

  const archiveOption = (
    definition: OpportunityFieldDefinition,
    optionId: string,
    optionLabel: string,
  ) => run(`archive-option:${optionId}`, async (generation) => {
    if (!settings || !canManage) return;
    const raw = await loader.updateOpportunityField(projectId, definition.id, {
      version: settings.version,
      archive_option_ids: [optionId],
    });
    if (generation !== projectGeneration.current) return;
    const parsed = parseOpportunityFieldSettings(raw, projectId);
    if (parsed === null) throw new Error("settings_response_invalid");
    onCatalogChanged(parsed);
    setNotice(
      `Opcija „${optionLabel}“ arhivirana je. Postojeće vrijednosti ostaju sačuvane.`,
    );
  });

  const changeVisible = (key: string) => {
    if (key === "builtin:organization") return;
    const hidden = usableLayout.hidden.includes(key)
      ? usableLayout.hidden.filter((entry) => entry !== key)
      : [...usableLayout.hidden, key];
    onLayout({ order: usableLayout.order, hidden });
  };

  return (
    <section aria-label="Timski podaci i raspored"
             className="space-y-3 rounded border border-border bg-muted/10 p-4"
             data-testid="opportunity-fields-section">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold">Timski podaci i raspored</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Zajednička polja i vrijednosti pripadaju projektu. Ručne vrijednosti su
            <strong> Unos tima</strong>; nisu provjereni dokaz i ne mijenjaju analizu,
            klasifikaciju ni pravo na odobrenje ili slanje.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <p className="rounded border border-border bg-background px-2 py-1 text-xs"
             data-testid="opportunity-layout-mode">
            Prikaz: <strong>{viewLabel}</strong>
          </p>
          <OpportunityPanelToggle
            controls="opportunity-fields-panel"
            expanded={!collapsed}
            label="Timski podaci i raspored"
            testId="opportunity-fields-toggle"
            onToggle={() => setCollapsed((value) => !value)}
          />
        </div>
      </div>

      <p className="break-words text-xs text-muted-foreground"
         role={error ? "alert" : "status"}
         data-testid="opportunity-fields-summary">
        {error ? (
          <span className="text-destructive">
            {errorCode === "version_conflict"
              ? "Sukob verzije: "
              : "Pogreška: "}
            {error}
          </span>
        ) : pending ? (
          <>Spremanje u tijeku: {savingLabel}…</>
        ) : busy ? (
          <>Učitavanje timskih polja i rasporeda…</>
        ) : (
          <>
            {notice && <span>{notice} </span>}
            {dirty ? (
              <>Nespremljene izmjene rasporeda ili unosa. Otvorite panel za pregled i spremanje.</>
            ) : !notice ? (
              <>Nema nespremljenih izmjena.</>
            ) : null}
          </>
        )}
      </p>

      <div id="opportunity-fields-panel" hidden={collapsed} className="space-y-3">
        {busy && (
          <p className="text-xs text-muted-foreground"
             data-testid="opportunity-fields-loading">
            Učitavanje timskih polja i rasporeda…
          </p>
        )}
        {error && (
          <p className="text-sm text-destructive"
             data-testid="opportunity-fields-error">
            {error}
          </p>
        )}
        {notice && (
          <p className="text-sm text-muted-foreground"
             data-testid="opportunity-fields-notice">
            {notice}
          </p>
        )}

        <div className="space-y-2 rounded border border-border bg-background p-3">
        <p className="text-xs text-muted-foreground">
          Skriveni stupci samo su prikaz; ne skrivaju podatke ni prava. Organizacija,
          odabir i otvaranje detalja uvijek ostaju dostupni.
        </p>
        <ul className="space-y-1 text-sm">
          {usableLayout.order.map((key, index) => (
            <li key={key} className="flex flex-wrap items-center justify-between gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!usableLayout.hidden.includes(key)}
                  disabled={key === "builtin:organization" || Boolean(pending) || busy}
                  aria-label={`Prikaži stupac ${columnLabel(key, definitions)}`}
                  onChange={() => changeVisible(key)}
                />
                {columnLabel(key, definitions)}
                {key.startsWith("custom:") && (
                  <span className="rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
                    Unos tima
                  </span>
                )}
              </label>
              <div className="flex gap-1">
                <button
                  type="button"
                  className="rounded border border-border px-2 py-1 disabled:opacity-30"
                  aria-label={`Pomakni stupac ${columnLabel(key, definitions)} ranije`}
                  disabled={index <= 1 || Boolean(pending) || busy}
                  onClick={() => onLayout({
                    order: move(usableLayout.order, index, -1),
                    hidden: usableLayout.hidden,
                  })}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="rounded border border-border px-2 py-1 disabled:opacity-30"
                  aria-label={`Pomakni stupac ${columnLabel(key, definitions)} kasnije`}
                  disabled={index === 0 || index === usableLayout.order.length - 1
                    || Boolean(pending) || busy}
                  onClick={() => onLayout({
                    order: move(usableLayout.order, index, 1),
                    hidden: usableLayout.hidden,
                  })}
                >
                  ↓
                </button>
              </div>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-2 pt-1">
          <button
            type="button"
            className="rounded bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-40"
            data-testid="opportunity-layout-save-personal"
            disabled={Boolean(pending) || busy || !settings}
            onClick={() => void savePersonal()}
          >
            {pending === "personal" ? "Spremam osobni prikaz…" : "Spremi osobni prikaz"}
          </button>
          <button
            type="button"
            className="rounded border border-border px-3 py-2 text-sm disabled:opacity-40"
            data-testid="opportunity-layout-use-default"
            disabled={Boolean(pending) || busy || !settings}
            onClick={() => void resetToProjectDefault()}
          >
            Vrati se na projektni zadani prikaz
          </button>
          {canManage && (
            <button
              type="button"
              className="rounded border border-border px-3 py-2 text-sm disabled:opacity-40"
              data-testid="opportunity-layout-save-shared"
              disabled={Boolean(pending) || busy || !settings}
              onClick={() => void saveShared()}
            >
              Spremi zajednički zadani prikaz
            </button>
          )}
        </div>
      </div>

      {canManage && settings && (
        <details className="rounded border border-border bg-background p-3"
                 data-testid="opportunity-field-catalog">
          <summary className="cursor-pointer text-sm font-medium">
            Katalog timskih polja ({definitions.length}/20)
          </summary>
          <p className="mt-2 text-xs text-muted-foreground">
            Dodavanje, opcije i arhiviranje zajednički su i vidljivi timu. Arhivirana
            polja/opcije zadržavaju identitet i postojeće vrijednosti.
          </p>
          <ul className="mt-3 space-y-3">
            {definitions.map((definition) => (
              <li key={definition.id} className="rounded border border-border p-2 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>
                    {definition.label}
                    <span className="ml-2 font-mono text-[10px] text-muted-foreground">
                      {definition.type} · {definition.id.slice(0, 12)}…
                    </span>
                    {definition.archived && (
                      <span className="ml-2 text-xs text-warn">arhivirano</span>
                    )}
                  </span>
                  {!definition.archived && (
                    <button
                      type="button"
                      className="rounded border border-border px-2 py-1 text-xs disabled:opacity-40"
                      data-testid={`opportunity-field-archive-${definition.id}`}
                      disabled={Boolean(pending) || busy}
                      onClick={() => void archiveField(definition)}
                    >
                      Arhiviraj polje
                    </button>
                  )}
                </div>
                {definition.type === "select" && (
                  <div className="mt-2 space-y-1">
                    {definition.options.map((option) => (
                      <div key={option.id}
                           className="flex flex-wrap items-center justify-between gap-2 text-xs">
                        <span>
                          {option.label}
                          {option.archived && (
                            <span className="ml-2 text-warn">arhivirano</span>
                          )}
                        </span>
                        {!option.archived && !definition.archived && (
                          <button
                            type="button"
                            className="rounded border border-border px-2 py-1 disabled:opacity-40"
                            data-testid={`opportunity-option-archive-${option.id}`}
                            disabled={Boolean(pending) || busy}
                            onClick={() => void archiveOption(definition, option.id, option.label)}
                          >
                            Arhiviraj opciju
                          </button>
                        )}
                      </div>
                    ))}
                    {!definition.archived && (
                      <div className="flex flex-wrap items-end gap-2 pt-1">
                        <input
                          className={inputClass}
                          maxLength={MAX_LABEL}
                          aria-label={`Nova opcija za ${definition.label}`}
                          value={optionDrafts[definition.id] ?? ""}
                          disabled={Boolean(pending) || busy}
                          onChange={(event) => setOptionDrafts((old) => ({
                            ...old, [definition.id]: event.target.value,
                          }))}
                          placeholder="Nova opcija"
                        />
                        <button
                          type="button"
                          className="rounded border border-border px-2 py-1 text-xs disabled:opacity-40"
                          data-testid={`opportunity-option-add-${definition.id}`}
                          disabled={Boolean(pending) || busy
                            || definition.options.length >= MAX_OPTIONS}
                          onClick={() => void addOption(definition)}
                        >
                          Dodaj opciju
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>

          <div className="mt-3 space-y-2 rounded border border-border p-2">
            <p className="text-xs font-medium">Novo timsko polje</p>
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-xs">
                Naziv
                <input
                  className={`${inputClass} mt-1 block`}
                  maxLength={MAX_LABEL}
                  value={newLabel}
                  disabled={Boolean(pending) || busy}
                  aria-label="Naziv novog timskog polja"
                  onChange={(event) => setNewLabel(event.target.value)}
                />
              </label>
              <label className="text-xs">
                Tip
                <select
                  className={`${inputClass} mt-1 block`}
                  value={newType}
                  disabled={Boolean(pending) || busy}
                  aria-label="Tip novog timskog polja"
                  onChange={(event) => setNewType(event.target.value as OpportunityFieldType)}
                >
                  <option value="text">Tekst</option>
                  <option value="number">Broj</option>
                  <option value="date">Datum</option>
                  <option value="select">Odabir</option>
                </select>
              </label>
            </div>
            {newType === "select" && (
              <label className="block text-xs">
                Opcije (jedna po redu, 1–20)
                <textarea
                  className={`${inputClass} mt-1 block w-full`}
                  rows={3}
                  value={newOptions}
                  disabled={Boolean(pending) || busy}
                  aria-label="Opcije novog polja"
                  onChange={(event) => setNewOptions(event.target.value)}
                />
              </label>
            )}
            <button
              type="button"
              className="rounded bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-40"
              data-testid="opportunity-field-create"
              disabled={Boolean(pending) || busy || definitions.length >= 20}
              onClick={() => void addField()}
            >
              Dodaj timsko polje
            </button>
          </div>
        </details>
      )}
      </div>
    </section>
  );
}
